import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import UsersSelect from 'live/components/forms/ui/users-select'
import { cloneDeep, set } from 'lodash'
import moment from 'moment-timezone'
import { Asterisk } from '../common/components'
import { ACTION_PLAN_STATUS } from '../common/utils'
import { ResultContext } from './qualityAnalysis'

interface IProps extends ActionPlan {
    index: number
}
export default function ActionPlanForm({
    index,
    whatHappened,
    whatShouldBeDone,
    planResults,
    planStatus,
    whenItWillBeDone,
    whoDidIt,
    whoIsInCharge
}: IProps): JSX.Element {
    const { actionPlans, setActionPlans } = useContext(ResultContext)

    const [actionPlanDate, setActionPlanDate] = useState(
        moment(whenItWillBeDone || new Date().getTime()).format('YYYY-MM-DD')
    )
    const [actionPlanTime, setActionPlanTime] = useState(
        moment(whenItWillBeDone || new Date().getTime()).format('HH:mm')
    )

    function onChangeValue(
        propertyToChange: keyof ActionPlan,
        newValue: unknown,
        event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ): void {
        if (event) event.preventDefault()

        const updatedPlans = cloneDeep(actionPlans)
        set(updatedPlans, `[${index}].${propertyToChange}`, newValue)

        setActionPlans(updatedPlans)
    }

    function onDeletePlan(): void {
        const updatedPlans = cloneDeep(actionPlans)
        updatedPlans.splice(index, 1)

        setActionPlans(updatedPlans)
    }
    useEffect(() => {
        onChangeValue('whenItWillBeDone', new Date(actionPlanDate + ' ' + actionPlanTime).getTime())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [actionPlanDate, actionPlanTime])

    return (
        <StyledActionPlan className={`actionPlan`}>
            {index ? <div className={`actionPlan--devider`} /> : null}
            <div className={`actionPlan--header`}>
                <span className={`actionPlan--header__title`}>
                    {i18n('action plan')} {index + 1}
                </span>
                <div
                    tabIndex={0}
                    className={`actionPlan--header__statusBtn ${planStatus ? '' : ACTION_PLAN_STATUS.PENDING}`}
                >
                    {planStatus ? i18n(ACTION_PLAN_STATUS.DONE) : i18n(ACTION_PLAN_STATUS.PENDING)}
                    <i className="fas fa-caret-down" />
                    <ul className={`actionPlan--header__statusBtn--optionList`}>
                        <li className={`actionPlan--header__statusBtn--optionList__option`}>
                            <button
                                className={`actionPlan--header__statusBtn--optionList__option--done`}
                                onClick={event => onChangeValue('planStatus', true, event)}
                            >
                                {i18n(ACTION_PLAN_STATUS.DONE)}
                            </button>
                        </li>
                        <li className={`actionPlan--header__statusBtn--optionList__option`}>
                            <button
                                className={`actionPlan--header__statusBtn--optionList__option--pending`}
                                onClick={event => onChangeValue('planStatus', false, event)}
                            >
                                {i18n(ACTION_PLAN_STATUS.PENDING)}
                            </button>
                        </li>
                    </ul>
                </div>
                <button className={`actionPlan--header__trashBtn`} onClick={() => onDeletePlan()}>
                    <i className="far fa-trash-alt" />
                </button>
            </div>
            <div className="actionPlan--whatHappened">
                <label className="actionPlan--whatHappened__label">
                    {i18n('What happened?')} <Asterisk />
                    <textarea
                        className="actionPlan--whatHappened__input"
                        value={whatHappened}
                        onChange={event => onChangeValue('whatHappened', event.target.value)}
                        placeholder={i18n('Write here what happened')}
                    />
                </label>
            </div>
            <div className="actionPlan--whatShouldBeDone">
                <label className="actionPlan--whatShouldBeDone__label">
                    {i18n('What should be done?')} <Asterisk />
                    <textarea
                        className="actionPlan--whatShouldBeDone__input"
                        value={whatShouldBeDone}
                        onChange={event => onChangeValue('whatShouldBeDone', event.target.value)}
                        placeholder={i18n('Write here what should be done')}
                    />
                </label>
            </div>

            <div className="actionPlan--date">
                <label className="actionPlan--date__label">
                    {i18n('Date')} {planStatus ? <Asterisk /> : null}
                    <input
                        className="actionPlan--date__input"
                        type="date"
                        value={actionPlanDate}
                        onChange={event => {
                            setActionPlanDate(event.target.value)
                        }}
                    />
                </label>
            </div>
            <div className="actionPlan--time">
                <label className="actionPlan--time__label">
                    {i18n('Horário')} {planStatus ? <Asterisk /> : null}
                    <input
                        className="actionPlan--time__input"
                        type="time"
                        value={actionPlanTime}
                        onChange={event => {
                            setActionPlanTime(event.target.value)
                        }}
                    />
                </label>
            </div>
            <div className="actionPlan--whoDidIt">
                <label htmlFor="" className="actionPlan--whoDitIt__label">
                    {i18n('Who did It?')} {planStatus ? <Asterisk /> : null}
                    <UsersSelect
                        minCharQuery="1"
                        onChange={userData => {
                            const { value, label } = userData
                            onChangeValue('whoDidIt', userData ? { value, label } : null)
                        }}
                        selected={whoDidIt}
                    />
                </label>
            </div>
            <div className="actionPlan--result">
                <label className="actionPlan--result__label">
                    {i18n('Result')} {planStatus ? <Asterisk /> : null}
                    <textarea
                        className="actionPlan--result__input"
                        value={planResults}
                        onChange={event => onChangeValue('planResults', event.target.value)}
                        placeholder={i18n('Write here the achieved result')}
                    />
                </label>
            </div>
        </StyledActionPlan>
    )
}

const StyledActionPlan = styled.fieldset`
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    .actionPlan {
        &--devider {
            width: 100%;
            height: 3px;
            background: var(--gray-500);
        }
        &--header {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            border-bottom: 1px solid var(--gray-300);
            padding: 8px 24px;
            width: 100%;

            &__title {
                flex-grow: 1;
                font-family: ${({ theme }) => theme['font-semibold']};
                font-size: 16px;
                text-transform: uppercase;
            }
            &__statusBtn,
            &__trashBtn {
                background: white;
                border: none;
            }
            &__trashBtn {
                color: var(--red-700);
                margin-left: 24px;
            }
            &__statusBtn {
                padding: 4px 12px;
                display: flex;
                align-items: center;
                border-radius: 3px;
                background: var(--green-700);
                font-family: ${({ theme }) => theme['font-semibold']};
                color: var(--gray-0);
                text-transform: capitalize;
                position: relative;
                cursor: pointer;
                &.pending {
                    background: var(--red-700);
                }
                &:focus-within {
                    ul.actionPlan--header__statusBtn--optionList {
                        display: flex;
                    }
                }
                i {
                    margin-left: 8px;
                }
                &--optionList {
                    display: none;
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    transform: translateY(100%);
                    margin: 0;
                    flex-direction: column;
                    align-items: stretch;
                    width: 100%;
                    background: white;
                    border-top: 0;
                    &__option {
                        &--done,
                        &--pending {
                            width: 100%;
                            border: none;
                            color: var(--blue-500);
                            &:hover {
                                color: var(--gray-0);
                            }
                        }
                        &--done:hover {
                            background: var(--green-700);
                        }
                        &--pending:hover {
                            background: var(--red-700);
                        }
                    }
                }
            }
        }
        &--date,
        &--time {
            width: 50%;
            padding: 15px;
            border-bottom: 1px solid var(--gray-300);
        }
        &--whatHappened,
        &--whatShouldBeDone,
        &--whoIsInCharge {
            &__::after {
                content: '*';
                color: red;
            }
        }
        &--whatHappened,
        &--whatShouldBeDone,
        &--whoIsInCharge,
        &--whoDidIt,
        &--result {
            padding: 15px;
            text-transform: capitalize;
            border-bottom: 1px solid var(--gray-300);
            width: 100%;
            &__input {
                border: none;
                margin-top: 10px;
                box-shadow: none;
            }
        }
        &--date,
        &--time {
            border-right: 1px solid var(--gray-300);
            &__input {
                border: none;
                font-size: 13px;
                margin-top: 10px;
                width: 100%;
                height: 33px;
            }
        }
        &--whoIsInCharge,
        &--whoDidIt {
            .Select {
                margin-top: 10px;
            }
            .Select-control {
                box-shadow: none;
                border: none;
            }
        }
        @media (max-width: 1200px) {
            &--time,
            &--date {
                width: 100%;
                border-right: none;
            }
        }
    }
`
